import type { WealthicaConnection } from '@/providers/wealthica2/types';
import type { ConnectionState } from '../multiWealthica2Store';
import { resolveStableIdentifier } from '@/providers/wealthica2/utils/identifiers';
import { createInitialConnectionState } from './state-utils';
import { storeLogger, EVENTS } from './logging';

// Local copy of status union to avoid circular imports
type ConnectionStatusType =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'loading_accounts'
  | 'disconnected'
  | 'error'
  | 'pending';

type LocalState = {
  connections: Map<string, ConnectionState>;
  activeConnectionId: string | null;
  persistDataPreference?: boolean;
  isInitializing?: boolean;
  initializationError?: string | null;
  isFetchingAllConnections?: boolean;
  lastBatchFetch?: Date | null;
  restorationAttempted?: boolean;
  restorationInProgress?: boolean;
  restorationSource?: string | null;
  hydrationCompleted?: boolean;
};

type SetFn = (partial: Partial<LocalState> | ((state: LocalState) => Partial<LocalState>)) => void;
type GetFn = () => LocalState & { setConnectionError: (institutionId: string, error: string | null) => void };

export const createConnectionActions = (set: unknown, get: unknown) => ({
  // === CONNECTION MANAGEMENT ===
  addConnection: (institutionId: string, connectionData: WealthicaConnection, bearerToken: string) => {
    const debugId = `add-conn-${Date.now()}-${institutionId.slice(-6)}`;
    // Server-only token model: frontend must not read or persist bearer tokens
    const token = '';

    console.log('🔗 MULTI-STORE: Adding connection attempt', {
      debugId,
      institutionId,
      providerName: connectionData?.providerName,
      hasExisting: (get as GetFn)().connections.has(institutionId)
    });

    try {
      // Provider-agnostic stable identifier to consolidate rotating IDs
      const stableIdentifier = resolveStableIdentifier({
        providerName: connectionData?.providerName,
        institutionIdentifier: connectionData?.institutionIdentifier,
        institutionId
      });

      // 1) If we already have an entry under THIS rotating OAuth ID, update it
      const existingConnection = (get as GetFn)().connections.get(institutionId) as ConnectionState | undefined;
      if (existingConnection) {
        console.log('🔄 MULTI-STORE: Updating existing connection instead of creating duplicate', {
          debugId,
          institutionId,
          existingProvider: existingConnection.providerName,
          newProvider: connectionData?.providerName
        });

        (set as SetFn)((state) => {
          const newConnections = new Map(state.connections);
          const updatedConnection = {
            ...existingConnection,
            connectionData,
            bearerToken: token || '',
            institutionId, // ensure institutionId matches key
            status: 'connected' as ConnectionStatusType,
            isConnected: true,
            error: null
          };
          newConnections.set(institutionId, updatedConnection);
          return {
            connections: newConnections,
            activeConnectionId: institutionId
          };
        });
        // Removed immediate targeted fetch here to prevent overlap with post-hydration forced fetch.
        return;
      }

      // 2) Provider-agnostic duplicate detection across rotating IDs via stable identifier
      try {
        const duplicates = Array.from(((get as GetFn)().connections.entries() as Iterable<[string, ConnectionState]>)).filter(([key, value]) => {
          if (key === institutionId) return false;
          const otherStable = resolveStableIdentifier({
            providerName: value?.providerName,
            institutionIdentifier: value?.connectionData?.institutionIdentifier,
            institutionId: key
          });
          return otherStable === stableIdentifier;
        });
        if (duplicates.length > 0) {
          console.log('🔄 MULTI-STORE: Consolidating rotating IDs by stable identifier', {
            debugId,
            stableIdentifier,
            newKey: institutionId,
            oldKeys: duplicates.map(([k]) => k)
          });
          (set as SetFn)((state) => {
            const newConnections = new Map(state.connections);
            // Merge prior state if any (prefer the last duplicate seen)
            let mergedPrev: ConnectionState | null = null;
            for (const [oldKey, prevState] of duplicates as [string, ConnectionState][]) {
              newConnections.delete(oldKey);
              mergedPrev = prevState;
              try {
                import('../../providers/wealthica2/utils/cache-management').then(({ rekeyInstitutionCaches }) => {
                  rekeyInstitutionCaches(oldKey, institutionId);
                  storeLogger.info('multiWealthica2Store', EVENTS.CACHE_REKEY, {
                    stableIdentifier,
                    to: institutionId
                  });
                }).catch(() => { /* noop */ });
              } catch (e) { /* noop */ }
            }
            // Normalize accounts on consolidated connection to the new rotating ID
            const base = (mergedPrev || createInitialConnectionState(connectionData, token || '', institutionId)) as unknown as Record<string, unknown>;
            const prevAccounts = (base['accounts'] as unknown[]) || [];
            const normalizedAccounts = prevAccounts.length > 0
              ? prevAccounts.map((acc: any) => {
                  const instObj = acc?.institution && typeof acc.institution === 'object' ? acc.institution : {};
                  return {
                    ...acc,
                    institutionId,
                    institution: { ...instObj, id: institutionId },
                    lastSyncAt: acc?.lastSyncAt || null
                  };
                })
              : prevAccounts;

            newConnections.set(institutionId, {
              ...(base as any),
              accounts: normalizedAccounts as unknown[],
              connectionData,
              bearerToken: token || '',
              institutionId,
              providerName: connectionData?.providerName,
              status: 'connected' as ConnectionStatusType,
              isConnected: true,
              error: null
            });
            return {
              connections: newConnections,
              activeConnectionId: state.activeConnectionId && duplicates.some(([k]) => k === state.activeConnectionId)
                ? institutionId
                : (state.activeConnectionId || institutionId)
            };
          });
          // Removed immediate targeted fetch here to prevent overlap with post-hydration forced fetch.
          return;
        }
      } catch (e) {
        console.warn('⚠️ MULTI-STORE: Failed consolidating rotating IDs', { error: e });
      }

      // 3) Normal path: create a brand new connection entry
      const newConnectionState = createInitialConnectionState(connectionData, token || '', institutionId);

      (set as SetFn)((state) => {
        const newConnections = new Map(state.connections);
        newConnections.set(institutionId, newConnectionState);
        return {
          connections: newConnections,
          activeConnectionId: state.activeConnectionId || institutionId
        };
      });

      console.log('✅ MULTI-STORE: Successfully added new connection', {
        debugId,
        institutionId,
        totalConnections: (get as GetFn)().connections.size
      });

      // Removed immediate targeted fetch here to prevent overlap with post-hydration forced fetch.
    } catch (error: unknown) {
      console.error('🚨 MULTI-STORE: Error adding connection', {
        debugId,
        institutionId,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
    }
  },

  removeConnection: (institutionId: string) => {
    console.log('🗑️ Removing connection:', institutionId);

    (set as SetFn)((state) => {
      const newConnections = new Map(state.connections);
      newConnections.delete(institutionId);

      return {
        connections: newConnections,
        activeConnectionId: state.activeConnectionId === institutionId 
          ? (newConnections.size > 0 ? Array.from(newConnections.keys())[0] : null)
          : state.activeConnectionId
      };
    });
  },

  getConnection: (institutionId: string): ConnectionState | undefined => {
    return (get as GetFn)().connections.get(institutionId) as ConnectionState | undefined;
  },

  getAllConnections: (): ConnectionState[] => {
    return Array.from((get as GetFn)().connections.values()) as ConnectionState[];
  },

  hasConnection: (institutionId: string): boolean => {
    return (get as GetFn)().connections.has(institutionId);
  },

  // === CONNECTION STATUS MANAGEMENT ===
  setConnectionStatus: (institutionId: string, status: ConnectionStatusType) => {
    const debugId = `status-${Date.now()}-${institutionId.slice(-6)}`;

    (set as SetFn)((state) => {
      const connection = state.connections.get(institutionId);
      if (!connection) return state;

      const newConnections = new Map(state.connections);
      newConnections.set(institutionId, {
        ...connection,
        status,
        isConnected: status === 'connected'
      });
      return { connections: newConnections };
    });

    storeLogger.info('multiWealthica2Store', EVENTS.CONNECTION_STATUS_UPDATED, {
      debugId,
      institutionId,
      status
    });
  },

  setConnectionError: (institutionId: string, error: string | null) => {
    (set as SetFn)((state) => {
      const connection = state.connections.get(institutionId);
      if (!connection) return state;

      const newConnections = new Map(state.connections);
      newConnections.set(institutionId, {
        ...connection,
        error
      });

      return { connections: newConnections };
    });
  },

  clearConnectionError: (institutionId: string) => {
    (get as GetFn)().setConnectionError(institutionId, null);
  },

  // === GLOBAL ACTIONS ===
  setActiveConnection: (institutionId: string | null) => {
    const debugId = `active-${Date.now()}-${institutionId?.slice(-6) || 'null'}`;
    const currentState = (get as GetFn)();

    storeLogger.info('multiWealthica2Store', EVENTS.ACTIVE_CONNECTION_SET, {
      phase: 'start',
      debugId,
      previousActive: currentState.activeConnectionId,
      newActive: institutionId,
      connectionExists: institutionId ? currentState.connections.has(institutionId) : false,
      totalConnections: currentState.connections.size,
      timestamp: new Date().toISOString()
    });

    (set as SetFn)({ activeConnectionId: institutionId });

    storeLogger.info('multiWealthica2Store', EVENTS.ACTIVE_CONNECTION_SET, {
      phase: 'complete',
      debugId,
      finalActive: institutionId,
      timestamp: new Date().toISOString()
    });
  },

  clearAllData: () => {
    const debugId = `clear-${Date.now()}`;
    const currentState = (get as GetFn)();

    console.log('🧹 Clearing all store data');

    (set as SetFn)({
      connections: new Map(),
      activeConnectionId: null
    });
  },

  resetState: () => {
    const debugId = `reset-${Date.now()}`;
    const currentState = (get as GetFn)();

    console.log('🔄 Resetting store state');

    (set as SetFn)({
      connections: new Map(),
      activeConnectionId: null,
      persistDataPreference: true,
      isInitializing: false,
      initializationError: null,
      isFetchingAllConnections: false,
      lastBatchFetch: null,
      restorationAttempted: false,
      restorationInProgress: false,
      restorationSource: null,
      hydrationCompleted: false
    });
  }
});
